import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Brain, Target, FlaskConical, BookOpen, Users, Shield, GraduationCap, Award, Heart, Phone, Mail, MapPin, ArrowRight } from 'lucide-react';
import CrisisSupport from '../components/CrisisSupport';
import ScrollIndicator from '../components/ScrollIndicator';
import AvailabilityStrip from '../components/AvailabilityStrip';
import SEO from '../components/SEO';
import './Home.css';

const Home = () => {
  // The business structured data (MedicalClinic) is sitewide in public/index.html.

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) entry.target.classList.add('revealed');
      });
    }, { threshold: 0.1, rootMargin: '0px 0px -50px 0px' });
    const elements = document.querySelectorAll('.scroll-reveal, .scroll-reveal-stagger');
    elements.forEach((el) => observer.observe(el));
    return () => elements.forEach((el) => observer.unobserve(el));
  }, []);

  return (
    <div className="page-home">
      <SEO
        title="Autism, ADHD & Learning Assessments | Clinical Minds Psychology | Coffs Harbour & Australia-wide"
        description="Neurodevelopmental assessments for children, teens and adults: autism, ADHD, FASD, learning and intellectual disability. Every assessment done personally by Lachlan Beaton, Clinical Psychologist (Registrar). Coffs Harbour base, visiting clinics and telehealth Australia-wide."
        canonicalUrl="https://www.clinicalminds.net"
      />
      <ScrollIndicator />

      {/* Hero Section */}
      <section id="main-content" className="hero-section">
        <div className="container hero-content">
          <div className="hero-text">
            <h1 className="heading-hero hero-animated">
              <span className="hero-word hero-word-psychologist">Psychological assessment and therapy</span>
              {' '}
              <span className="hero-word hero-word-location">in Coffs Harbour</span>
            </h1>
            <p className="body-large hero-subtitle hero-subline">
              Based in Coffs Harbour, assessing young people and adults Australia-wide, with regular clinics in Port Macquarie, Sydney, Melbourne, the Gold Coast and Hamilton in western Victoria.
            </p>
            <p className="body-large hero-subtitle hero-services">
              Autism | ADHD | FASD | Learning | Intellectual Disability | Therapy
            </p>
            <p className="body-large hero-subtitle hero-credentials">
              <strong>Lachlan Beaton</strong>, Clinical Psychologist (Registrar)
            </p>
            {/* The availability strip and the two buttons share one block, so the
                strip's hairlines are always exactly as wide as the buttons */}
            <div className="hero-cta-block" data-testid="hero-cta-block">
              <AvailabilityStrip />
              <div className="hero-actions">
                <Link to="/contact?enquiry=Assessment" className="btn-primary" data-testid="hero-enquiry-cta">Enquire About an Assessment</Link>
                <Link to="/what-to-expect" className="btn-secondary" data-testid="hero-how-cta">How Assessment Works</Link>
              </div>
            </div>
          </div>
          <div className="hero-photo-card">
            <picture>
              <source
                type="image/webp"
                srcSet="/images/photos/lachlan-headshot-480w.webp 480w, /images/photos/lachlan-headshot.webp 800w"
                sizes="(max-width: 768px) 280px, 400px"
              />
              <img
                src="/images/photos/lachlan-headshot.jpg"
                alt="Lachlan Beaton, Clinical Psychologist (Registrar), seated in his office in Coffs Harbour"
                className="hero-photo"
                width="400"
                height="600"
                fetchPriority="high"
                data-testid="hero-psychologist-photo"
              />
            </picture>
          </div>
        </div>
      </section>

      {/* What We Assess */}
      <section className="section-assessments" data-testid="what-we-assess-section">
        <div className="container">
          <div className="section-header">
            <span className="section-label">Assessments</span>
            <h2 className="heading-1">What I Assess</h2>
            <p className="body-large" style={{ margin: '0 auto' }}>Comprehensive assessments for children, adolescents and adults, in Coffs Harbour, at visiting clinics, and Australia-wide.</p>
          </div>
          <div className="assess-cards-grid">
            <Link to="/autism-assessment" className="assess-card" data-testid="assess-card-autism">
              <div className="assess-card-icon"><Brain size={32} strokeWidth={1.5} /></div>
              <h3>Autism</h3>
              <p>Understanding how you relate to the world and the people in it.</p>
              <span className="assess-card-link">Learn more <ArrowRight size={14} /></span>
            </Link>
            <Link to="/adhd-assessment" className="assess-card" data-testid="assess-card-adhd">
              <div className="assess-card-icon"><Target size={32} strokeWidth={1.5} /></div>
              <h3>ADHD</h3>
              <p>Assessing attention, focus and executive functioning.</p>
              <span className="assess-card-link">Learn more <ArrowRight size={14} /></span>
            </Link>
            <Link to="/fasd-assessment" className="assess-card" data-testid="assess-card-fasd">
              <div className="assess-card-icon"><FlaskConical size={32} strokeWidth={1.5} /></div>
              <h3>FASD</h3>
              <p>Dedicated assessment for Fetal Alcohol Spectrum Disorder.</p>
              <span className="assess-card-link">Learn more <ArrowRight size={14} /></span>
            </Link>
            <Link to="/learning-assessment" className="assess-card" data-testid="assess-card-learning">
              <div className="assess-card-icon"><BookOpen size={32} strokeWidth={1.5} /></div>
              <h3>Learning</h3>
              <p>Identifying learning differences in reading, writing and maths.</p>
              <span className="assess-card-link">Learn more <ArrowRight size={14} /></span>
            </Link>
            <Link to="/learning-assessment#intellectual-disability" className="assess-card" data-testid="assess-card-intellectual">
              <div className="assess-card-icon"><Users size={32} strokeWidth={1.5} /></div>
              <h3>Intellectual Disability</h3>
              <p>Full cognitive and adaptive assessment where intellectual disability is a question.</p>
              <span className="assess-card-link">Learn more <ArrowRight size={14} /></span>
            </Link>
          </div>
        </div>
      </section>

      {/* How Assessment Works */}
      <section className="section-how-assessment scroll-reveal" data-testid="how-assessment-section">
        <div className="container">
          <div className="section-header">
            <span className="section-label">Getting Started</span>
            <h2 className="heading-1">How Assessment Works</h2>
          </div>
          <div className="timeline-steps">
            <div className="timeline-step">
              <div className="timeline-number">1</div>
              <div className="timeline-content">
                <h3 className="heading-3">Enquire</h3>
                <p className="body-medium">Call, email or use the contact form. We'll talk through what you're looking for and whether we're the right fit.</p>
              </div>
            </div>
            <div className="timeline-connector"></div>
            <div className="timeline-step">
              <div className="timeline-number">2</div>
              <div className="timeline-content">
                <h3 className="heading-3">Time, prioritised</h3>
                <p className="body-medium">We don't rush assessment. You'll have an intake session plus at least two assessment sessions on different days, because understanding someone properly takes time, and nobody meets the world the same way every day.</p>
              </div>
            </div>
            <div className="timeline-connector"></div>
            <div className="timeline-step">
              <div className="timeline-number">3</div>
              <div className="timeline-content">
                <h3 className="heading-3">Understanding</h3>
                <p className="body-medium">A plain-language feedback session and a full written report with practical recommendations.</p>
              </div>
            </div>
          </div>
          <div style={{ textAlign: 'center', marginTop: '2rem' }}>
            <Link to="/what-to-expect" className="btn-secondary">See the full journey</Link>
          </div>
        </div>
      </section>

      {/* The Difference */}
      <section className="section-difference scroll-reveal" data-testid="the-difference-section">
        <div className="container">
          <div className="difference-card">
            <div className="difference-photo-col">
              <img
                src="/images/photos/lachlan-headshot.jpg"
                alt="Lachlan Beaton, Clinical Psychologist (Registrar)"
                className="difference-photo"
                loading="lazy"
              />
            </div>
            <div className="difference-text-col">
              <span className="section-label">THE PERSONAL TOUCH</span>
              <h2 className="heading-1">Assessment, done personally</h2>
              <p className="body-medium">
                The person being assessed is the point of all of this. My aim is to understand someone's strengths and differences, and to build an affirming picture of how they best like to meet the world and learn. Everything about how I assess follows from that.
              </p>
              <p className="body-medium">
                It means an hour with parents or caregivers before any assessment begins. At least two assessment sessions on different days, because how someone meets the world on Tuesday isn't always how they meet it on Friday, and I want to see both. An hour of feedback where we talk through every result in plain language. And a report with practical recommendations for home, school or work, plus a personal profile written for the person who was assessed, about them, for them.
              </p>
              <p className="body-medium">
                Nothing is outsourced: every conversation, every session, every page of the report is mine. The person who meets you, or your child, is the person who writes the report.
              </p>
              <p className="difference-attribution">
                Lachlan Beaton, Clinical Psychologist (Registrar)
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Meet Your Psychologist */}
      <section className="section-meet">
        <div className="container">
          <div className="meet-content">
            <div className="meet-image">
              <img
                src="/images/photos/lachlan-portrait.jpg"
                alt="Lachlan Beaton, Clinical Psychologist (Registrar)"
                className="psychologist-photo"
                loading="lazy"
              />
            </div>
            <div className="meet-text">
              <span className="section-label">About</span>
              <h2 className="heading-1">Meet Lachlan Beaton</h2>
              <h3 className="heading-3 psychologist-name">Clinical Psychologist (Registrar)</h3>
              <p className="body-medium">
                I became a psychologist to give people the support I wanted when I was younger. Born in rural Australia, I'm passionate about bringing assessment and support to people who don't always have access to it.
              </p>
              <p className="body-medium">
                My weeks are spent mostly in assessment, working with young people and adults across the country, alongside therapy for neurodivergent people, teens, and people working through addiction.
              </p>
              <Link to="/about" className="btn-secondary">More About My Approach</Link>
            </div>
          </div>
        </div>
      </section>

      {/* Therapy */}
      <section className="section-therapy-banner scroll-reveal" data-testid="therapy-section">
        <div className="container">
          <div className="section-header">
            <span className="section-label">Therapy</span>
            <h2 className="heading-1">Therapy</h2>
          </div>
          <p className="body-large" style={{ maxWidth: '720px', margin: '0 auto 2rem', textAlign: 'center', lineHeight: 1.8 }}>
            Therapy at Clinical Minds is a service in its own right. Main areas of interest are neurodiversity (young people and adults), teens, and addiction, an area Lachlan has also written about as a <Link to="/about#publications" style={{ color: 'var(--blue)', textDecoration: 'underline' }}>published author</Link>. He also works with anxiety, depression, trauma and more.
          </p>
          <div style={{ textAlign: 'center' }}>
            <Link to="/therapy" className="btn-secondary">Explore Therapy</Link>
          </div>
        </div>
      </section>

      {/* Schools */}
      <section className="section-schools-banner scroll-reveal" data-testid="schools-section">
        <div className="container">
          <div className="schools-banner-card">
            <p className="body-large">The School Wellbeing Hub brings assessment and psychology on-site to rural and regional schools.</p>
            <Link to="/schools" className="btn-secondary">Learn about school partnerships</Link>
          </div>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="section-trust-badges">
        <div className="container">
          <div className="trust-badges">
            <div className="trust-badge">
              <Shield size={32} strokeWidth={1.5} />
              <div>
                <div className="trust-badge-title">AHPRA Registered Psychologist</div>
                <div className="trust-badge-subtitle">PSY0002785698</div>
              </div>
            </div>
            <div className="trust-badge">
              <GraduationCap size={32} strokeWidth={1.5} />
              <div>
                <div className="trust-badge-title">Clinical Qualifications</div>
                <div className="trust-badge-subtitle">Masters of Psychology (Clinical)</div>
              </div>
            </div>
            <div className="trust-badge">
              <Award size={32} strokeWidth={1.5} />
              <div>
                <div className="trust-badge-title">Evidence-Based</div>
                <div className="trust-badge-subtitle">Science-Backed Approaches</div>
              </div>
            </div>
            <div className="trust-badge">
              <Heart size={32} strokeWidth={1.5} />
              <div>
                <div className="trust-badge-title">Medicare Rebates</div>
                <div className="trust-badge-subtitle">Available for therapy with a Mental Health Treatment Plan</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="section-contact-home">
        <div className="container">
          <div className="section-header">
            <span className="section-label">Get in Touch</span>
            <h2 className="heading-1">Contact Us</h2>
          </div>
          <div className="contact-info-grid">
            <div className="contact-info-card">
              <div className="contact-icon"><Phone size={24} strokeWidth={1.5} /></div>
              <div className="contact-details">
                <h3 className="heading-3">Phone</h3>
                <p className="body-medium"><a href="tel:0266002710" className="contact-link">02 6600 2710</a></p>
                <p className="body-medium"><a href="tel:0415220944" className="contact-link">0415 220 944</a></p>
              </div>
            </div>
            <div className="contact-info-card">
              <div className="contact-icon"><Mail size={24} strokeWidth={1.5} /></div>
              <div className="contact-details">
                <h3 className="heading-3">Email</h3>
                <p className="body-medium"><a href="mailto:contact@clinicalminds.net" className="contact-link">contact@clinicalminds.net</a></p>
              </div>
            </div>
            <div className="contact-info-card">
              <div className="contact-icon"><MapPin size={24} strokeWidth={1.5} /></div>
              <div className="contact-details">
                <h3 className="heading-3">Location</h3>
                <p className="body-medium">
                  <a href="https://www.google.com/maps/dir//76+Albany+Street,+Coffs+Harbour+NSW+2450" target="_blank" rel="noopener noreferrer" className="contact-link">
                    76 Albany Street<br />Coffs Harbour NSW 2450
                  </a>
                </p>
              </div>
            </div>
          </div>
          <div className="contact-cta">
            <Link to="/contact?enquiry=Assessment" className="btn-primary btn-contact" data-testid="homepage-assessment-enquiry-cta">Enquire About an Assessment</Link>
            <Link to="/bookings" className="btn-secondary btn-contact" data-testid="homepage-book-appointment-cta">Request an Appointment</Link>
          </div>
        </div>
      </section>

      <CrisisSupport />
    </div>
  );
};

export default Home;
